import { useCallback, useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'

const LIMIT_SIZE = 0 // povodna hodnota 767.98

declare global {
  interface Window {
    smartsupp?: (command: 'chat:hide' | 'chat:show') => void
  }
}

/**
 * Routes where smartsupp chat should be permanently visible
 */
const ALWAYS_VISIBLE_ROUTES = ['register', 'login', 'forgot-password']

/**
 * Check if current route is in the list where smartsupp should be permanently visible
 */
function shouldShowChatPermanently(pathname: string): boolean {
  const routeName = pathname.split('/').filter(Boolean)[0] || pathname
  return ALWAYS_VISIBLE_ROUTES.includes(routeName)
}

/**
 * Initialize smartsupp chat widget with responsive behavior
 *
 * Features:
 * - Hides chat on mobile devices (< 768px) except on specific routes
 * - Shows/hides chat based on window resize
 * - Monitors route changes to show/hide chat accordingly
 * - Monitors chat window size changes
 */
export function useSmartsupp() {
  const location = useLocation()
  const intervalRef = useRef<number | null>(null)
  const matchMediaRef = useRef<MediaQueryList | null>(null)
  const handleMediaChangeRef = useRef<
    ((event: MediaQueryListEvent | MediaQueryList) => void) | null
  >(null)
  const prevRouteRef = useRef<string | null>(null)
  const prevWidthRef = useRef<number>(0)
  const appElementRef = useRef<HTMLElement | null>(null)
  const isMobileRef = useRef<boolean>(document.body.clientWidth <= LIMIT_SIZE)

  const initSmartsupp = useCallback(() => {
    if (!window.smartsupp) return

    const matchMedia = window.matchMedia(`(max-width: ${LIMIT_SIZE}px)`)
    matchMediaRef.current = matchMedia
    isMobileRef.current = document.body.clientWidth <= LIMIT_SIZE

    // Handle window resize - store in ref so it's available in cleanup
    const handleMediaChange = (event: MediaQueryListEvent | MediaQueryList) => {
      isMobileRef.current = event.matches
      const shouldShow = !shouldShowChatPermanently(location.pathname)

      if (window.smartsupp && shouldShow) {
        window.smartsupp(event.matches ? 'chat:hide' : 'chat:show')
      }
    }

    handleMediaChangeRef.current = handleMediaChange

    // Modern browsers
    try {
      if (matchMedia.addEventListener) {
        matchMedia.addEventListener('change', handleMediaChange)
      } else {
        // Fallback for older Safari
        matchMedia.addListener(handleMediaChange)
      }
    } catch (ex) {
      console.error('Error setting up media query listener:', ex)
    }

    // Monitor route changes and chat window size
    intervalRef.current = window.setInterval(() => {
      if (!window.smartsupp) {
        if (intervalRef.current) {
          clearInterval(intervalRef.current)
        }
        return
      }

      // Get chat application element
      if (!appElementRef.current) {
        appElementRef.current = document.getElementById('chat-application')
      }

      const currentRoute = location.pathname
      const shouldShowPermanently = shouldShowChatPermanently(currentRoute)

      // Handle route changes
      if (prevRouteRef.current !== currentRoute) {
        if (window.smartsupp) {
          const shouldShow = !shouldShowPermanently && !isMobileRef.current
          window.smartsupp(shouldShow ? 'chat:show' : 'chat:hide')
        }
        prevRouteRef.current = currentRoute
      }

      // Handle chat window size changes (when user closes chat)
      if (appElementRef.current) {
        const currWidth = appElementRef.current.clientWidth
        if (prevWidthRef.current - currWidth > 200) {
          if (isMobileRef.current && !shouldShowPermanently) {
            if (window.smartsupp) {
              window.smartsupp('chat:hide')
            }
          }
        }
        prevWidthRef.current = currWidth
      }
    }, 500)
  }, [location.pathname])

  useEffect(() => {
    // Wait for smartsupp to be available
    let checkCounter = 0
    const checkExist = setInterval(() => {
      if (window.smartsupp || checkCounter > 60) {
        initSmartsupp()
        clearInterval(checkExist)
      }
      checkCounter++
    }, 100)

    // Cleanup
    return () => {
      clearInterval(checkExist)

      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }

      if (matchMediaRef.current && handleMediaChangeRef.current) {
        try {
          if (matchMediaRef.current.removeEventListener) {
            matchMediaRef.current.removeEventListener(
              'change',
              handleMediaChangeRef.current
            )
          } else {
            matchMediaRef.current.removeListener(handleMediaChangeRef.current)
          }
        } catch (ex) {
          console.error('Error removing media query listener:', ex)
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname])
}
